import React, { useEffect, useMemo } from "react";
import { View, StyleSheet, ScrollView, Linking } from "react-native";

import CustomText from "../../../common/general/CustomText/CustomText";
import ProjectGradingSection from "./ProjectGradingSection";
import {
  GetProjectSubmissionDocument,
  ProjectRubricScoreInput,
  useAllowLateProjectSubmissionMutation,
  useClearProjectSubmissionStatusMutation,
  useExcuseProjectSubmissionMutation,
  useGetProjectSubmissionQuery,
  useMarkProjectSubmissionMissingMutation,
} from "@axio/graphql/generated/graphql";
import { useGradingCenterSubmissions } from "../../../../contexts/GradingCenterContext";
import FileSubmissionItem from "./StudentSubmission/FileSubmissionItem";
import { formatSubmittedDate, formatSubmittedTime } from "../../../../utils/date";
import StudentNavigationPanel from "./StudentNavigationPanel";
import { GradingCenterModalState } from "../types";
import { spacing } from "../../../../styles/design-system";
import { DotsThreeIcon as DotsThreeBase } from "@phosphor-icons/react";
import { PopoverMenu } from "../../../common/general/PopoverMenu/PopoverMenu";

const DotsThreeIcon = DotsThreeBase as unknown as React.ComponentType<{ size?: number; color?: string }>;

const projectPayloadEqual = (
  current: { grade: number | null; feedback: string; rubricScores?: ProjectRubricScoreInput[] },
  initial: { grade: number | null; feedback: string; rubricScores?: ProjectRubricScoreInput[] } | null
): boolean => {
  if (!initial) return false;
  if (current.grade !== initial.grade) return false;
  if (current.feedback !== initial.feedback) return false;
  const sortById = (a: ProjectRubricScoreInput, b: ProjectRubricScoreInput) =>
    a.criterionId.localeCompare(b.criterionId);
  const cur = JSON.stringify([...(current.rubricScores ?? [])].sort(sortById));
  const ini = JSON.stringify([...(initial.rubricScores ?? [])].sort(sortById));
  return cur === ini;
};

const getStatusColor = (status: string) => {
  switch (status) {
    case "COMPLETED":
    case "Completed":
      return { bg: "#d1fae5", text: "#065f46" };
    case "NEEDS_REVIEW":
    case "Needs Review":
      return { bg: "#fecaca", text: "#dc2626" };
    case "APPROVED":
      return { bg: "#dbeafe", text: "#1e40af" };
    case "REJECTED":
      return { bg: "#fee2e2", text: "#991b1b" };
    default:
      return { bg: "#f3f4f6", text: "#6b7280" };
  }
};

interface ProjectGradingPanelProps {
  setModalState: (state: GradingCenterModalState) => void;
}

const ProjectGradingPanel: React.FC<ProjectGradingPanelProps> = ({ setModalState }) => {
  const { selectedSubmissionData, updatedProjectPayload, setUpdatedProjectPayload, setCanSaveProject } =
    useGradingCenterSubmissions();

  const { data: projectSubmissionDetails } = useGetProjectSubmissionQuery({
    variables: { id: selectedSubmissionData?.id || "" },
    skip: !selectedSubmissionData?.id || selectedSubmissionData?.submissionType !== "Project",
  });

  const projectSubmission = projectSubmissionDetails?.getProjectSubmission?.data;
  const project = projectSubmission?.project;

  const initialProjectPayload = useMemo(() => {
    if (!projectSubmission) return null;
    const grade = projectSubmission.grade ?? selectedSubmissionData?.grade ?? null;
    const feedback = projectSubmission.feedback ?? "";
    const rubricScores =
      projectSubmission.rubricScores?.map((s) => ({
        criterionId: s.criterionId,
        criterionName: s.criterionName,
        earnedPoints: s.earnedPoints,
        maxPoints: s.maxPoints,
      })) ?? [];
    return { grade, feedback, rubricScores };
  }, [projectSubmission, selectedSubmissionData?.grade]);

  useEffect(() => {
    if (initialProjectPayload && selectedSubmissionData?.id) {
      setUpdatedProjectPayload({ ...initialProjectPayload });
    }
  }, [initialProjectPayload, selectedSubmissionData?.id, setUpdatedProjectPayload]);

  useEffect(() => {
    const canSave =
      updatedProjectPayload != null &&
      updatedProjectPayload.grade != null &&
      !projectPayloadEqual(updatedProjectPayload, initialProjectPayload);
    setCanSaveProject(canSave);
  }, [updatedProjectPayload, initialProjectPayload, setCanSaveProject]);

  const grade = updatedProjectPayload?.grade ?? null;
  const feedback = updatedProjectPayload?.feedback ?? "";

  const handleGradeChange = (newGrade: number | null) => {
    setUpdatedProjectPayload({
      ...(updatedProjectPayload || { grade: null, feedback: "" }),
      grade: newGrade,
    });
  };

  const handleFeedbackChange = (newFeedback: string) => {
    setUpdatedProjectPayload({
      ...(updatedProjectPayload || { grade: null, feedback: "" }),
      feedback: newFeedback,
    });
  };

  const [excuseProjectSubmission] = useExcuseProjectSubmissionMutation();
  const [markProjectSubmissionMissing] = useMarkProjectSubmissionMissingMutation();
  const [allowLateProjectSubmission] = useAllowLateProjectSubmissionMutation();
  const [clearProjectSubmissionStatus] = useClearProjectSubmissionStatusMutation();

  const handleRubricScoresChange = (scores: ProjectRubricScoreInput[]) => {
    setUpdatedProjectPayload((prev) => ({ ...prev, rubricScores: scores }));
  };

  const refetchVars = { variables: { id: selectedSubmissionData?.id || "" } };
  const submissionDataId = selectedSubmissionData?.id ?? "";
  const refetchAfterStatusChange = [{ query: GetProjectSubmissionDocument, ...refetchVars }, "GetGradebook"];

  const statusMenuItems = useMemo(
    () => [
      {
        key: "missing",
        label: "Mark as Missing",
        onPress: () =>
          markProjectSubmissionMissing({
            variables: { submissionId: submissionDataId },
            refetchQueries: refetchAfterStatusChange,
          }),
      },
      {
        key: "excused",
        label: "Mark as Excused",
        onPress: () =>
          excuseProjectSubmission({
            variables: { submissionId: submissionDataId },
            refetchQueries: refetchAfterStatusChange,
          }),
      },
      {
        key: "late",
        label: "Allow Late",
        onPress: () =>
          allowLateProjectSubmission({
            variables: { submissionId: submissionDataId },
            refetchQueries: refetchAfterStatusChange,
          }),
      },
      {
        key: "clear",
        label: "Clear Status",
        onPress: () =>
          clearProjectSubmissionStatus({
            variables: { submissionId: submissionDataId },
            refetchQueries: refetchAfterStatusChange,
          }),
        danger: true,
      },
    ],
    [submissionDataId]
  );

  const statusColors = getStatusColor(selectedSubmissionData?.status || "");

  return (
    <ScrollView style={styles.content} showsVerticalScrollIndicator={false} tabIndex={0}>
      <StudentNavigationPanel setModalState={setModalState} />
      {/* Project Info Header */}
      <View style={styles.projectInfoHeader}>
        <View style={styles.projectBasicInfo}>
          <CustomText
            text={project?.name ?? selectedSubmissionData?.submissionTitle}
            size="l"
            weight="bold"
            style={styles.projectTitle}
            useTranslationText={false}
          />
          <View style={styles.projectMeta}>
            {selectedSubmissionData?.courseName && (
              <View style={styles.metaTag}>
                <CustomText text={selectedSubmissionData.courseName} size="xs" useTranslationText={false} />
              </View>
            )}
            {selectedSubmissionData?.moduleName && (
              <View style={styles.metaTag}>
                <CustomText text={selectedSubmissionData.moduleName} size="xs" useTranslationText={false} />
              </View>
            )}
            {project?.dueDate && (
              <View style={styles.metaTag}>
                <CustomText
                  text={`Due: ${new Date(project.dueDate).toLocaleDateString()}`}
                  size="xs"
                  useTranslationText={false}
                />
              </View>
            )}
          </View>
        </View>
        <View style={styles.projectStatusInfo}>
          <View style={styles.submissionStatus}>
            <View style={[styles.badge, { backgroundColor: statusColors.bg }]}>
              <CustomText
                text={selectedSubmissionData?.status}
                useTranslationText={false}
                size="xs"
                style={[styles.statusText, { color: statusColors.text }]}
              />
            </View>
          </View>
        </View>
        <PopoverMenu
          items={statusMenuItems}
          placement="bottom-end"
          triggerAccessibilityLabel="Submission actions"
          renderTrigger={() => <DotsThreeIcon size={20} color="#6B7280" />}
        />
      </View>

      {/* Student Info */}
      <View style={styles.studentSubmissionInfo}>
        <View style={styles.studentDetails}>
          <View style={styles.studentAvatar}>
            <CustomText
              text={selectedSubmissionData?.student?.initials}
              size="s"
              weight="bold"
              style={styles.avatarText}
              useTranslationText={false}
            />
          </View>
          <View style={styles.studentInfo}>
            <CustomText
              text={selectedSubmissionData?.student?.name}
              size="s"
              weight="bold"
              useTranslationText={false}
            />
            <CustomText
              text={selectedSubmissionData?.student?.email}
              size="xs"
              style={styles.email}
              useTranslationText={false}
            />
          </View>
        </View>
        <View style={styles.submissionDetails}>
          <CustomText
            text="submitted_on"
            size="xs"
            interpolation={{ value: formatSubmittedDate(selectedSubmissionData?.submittedOn) }}
          />
          <CustomText
            text="submitted_time"
            size="xs"
            interpolation={{ value: formatSubmittedTime(selectedSubmissionData?.submittedTime) }}
          />
        </View>
      </View>

      {/* Student Submission */}
      <View style={styles.studentSubmissionSection}>
        <CustomText text="Student Submission" weight="bold" style={styles.sectionTitle} />
        {projectSubmission?.submissionLink && (
          <View style={styles.submissionLinkContainer}>
            <CustomText
              text="Submission Link:"
              size="s"
              weight="bold"
              style={styles.linkLabel}
              useTranslationText={false}
            />
            <CustomText
              text={projectSubmission.submissionLink}
              size="s"
              style={styles.link}
              onPress={() => Linking.openURL(projectSubmission.submissionLink!)}
              useTranslationText={false}
            />
          </View>
        )}
        {projectSubmission?.submissionNotes && (
          <View style={styles.submissionNotesContainer}>
            <CustomText
              text="Submission Notes:"
              size="s"
              weight="bold"
              style={styles.notesLabel}
              useTranslationText={false}
            />
            <CustomText
              text={projectSubmission.submissionNotes}
              size="s"
              style={styles.notes}
              useTranslationText={false}
            />
          </View>
        )}
        {projectSubmission?.files && projectSubmission.files.length > 0 && (
          <View style={styles.filesContainer}>
            {projectSubmission.files.map((file) => (
              <FileSubmissionItem key={file.id} file={file} />
            ))}
          </View>
        )}
      </View>

      {/* Grading Section */}
      <ProjectGradingSection
        grade={grade}
        feedback={feedback}
        onGradeChange={handleGradeChange}
        onFeedbackChange={handleFeedbackChange}
        assessmentCriteria={project?.assessmentCriteria}
        onRubricScoresChange={handleRubricScoresChange}
        existingRubricScores={projectSubmission?.rubricScores}
      />
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  content: {
    flex: 1,
    padding: 24,
  },
  projectInfoHeader: {
    backgroundColor: "#F9FAFB",
    borderWidth: 1,
    borderColor: "#EAECF0",
    borderRadius: spacing.xs,
    padding: spacing.md,
    marginTop: spacing.sm,
    marginBottom: spacing.lg,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: spacing.md,
  },
  projectBasicInfo: {
    flex: 1,
  },
  projectTitle: {
    color: "#111827",
    marginBottom: spacing.sm,
  },
  projectMeta: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: spacing.sm,
  },
  metaTag: {
    backgroundColor: "#F2F4F7",
    borderWidth: 1,
    borderColor: "#EAECF0",
    borderRadius: spacing.xxs,
    paddingHorizontal: spacing.xs,
    paddingVertical: spacing.xxs,
  },
  projectStatusInfo: {
    alignItems: "flex-end",
  },
  submissionStatus: {
    alignItems: "flex-end",
    gap: 8,
  },
  badge: {
    paddingHorizontal: spacing.xs,
    paddingVertical: spacing.xxs,
    borderRadius: spacing.sm,
    alignSelf: "flex-start",
  },
  statusText: {
    letterSpacing: 0.5,
    textTransform: "uppercase",
  },
  studentSubmissionInfo: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: "#EFF6FF",
    borderWidth: 1,
    borderColor: "#BFDBFE",
    borderRadius: spacing.xs,
    padding: spacing.sm,
    marginBottom: spacing.lg,
  },
  studentDetails: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.sm,
  },
  studentAvatar: {
    width: spacing["3xl"],
    height: spacing["3xl"],
    borderRadius: spacing["3xl"] / 2,
    backgroundColor: "#3B82F6",
    justifyContent: "center",
    alignItems: "center",
  },
  avatarText: {
    color: "#FFFFFF",
  },
  studentInfo: {
    flex: 1,
  },
  email: {
    color: "#6B7280",
    marginTop: spacing.xxs,
  },
  submissionDetails: {
    alignItems: "flex-end",
  },
  studentSubmissionSection: {
    marginBottom: spacing["2xl"],
  },
  sectionTitle: {
    color: "#374151",
    marginBottom: spacing.xxs,
  },
  submissionLinkContainer: {
    marginBottom: spacing.sm,
  },
  linkLabel: {
    color: "#374151",
    marginBottom: spacing.xxs,
  },
  link: {
    color: "#3B82F6",
    textDecorationLine: "underline",
  },
  submissionNotesContainer: {
    marginBottom: spacing.sm,
  },
  notesLabel: {
    color: "#374151",
    marginBottom: spacing.xxs,
  },
  notes: {
    color: "#6B7280",
  },
  filesContainer: {
    gap: spacing.sm,
  },
});

export default ProjectGradingPanel;
